import React, { useEffect, useMemo, useState } from 'react';
import { Box, Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';
import CommonBadge from './CommonBadge';
import CommonButton from './CommonButton';
import CommonDropdown from './CommonDropdown';
import CommonModal from './CommonModal';
import SearchableDropdown from './SearchableDropdown';
import { ApiError, bulkReassignUsers, getUsers, type BulkReassignRelation, type UserApiItem } from '../api';
import { showToast } from '../utils/toast';
import { CommonOption } from '../types/common.types';

export interface BulkReassignmentModalProps {
  open: boolean;
  onClose: () => void;
  onReassigned: () => void;
}

const RELATION_OPTIONS: { value: BulkReassignRelation; label: string; idField: keyof UserApiItem }[] = [
  { value: 'reporting_manager', label: 'Reporting Manager', idField: 'reporting_manager_id' },
  { value: 'reporting_lead', label: 'Reporting Lead', idField: 'reporting_lead_id' },
  { value: 'qc', label: 'QC / SME', idField: 'qc_id' },
  { value: 'dispute_person', label: 'Dispute Person', idField: 'dispute_person_id' },
];

const toOption = (user: UserApiItem): CommonOption => ({
  value: user._id,
  label: `${user.name} (${user.role_name}${user.meta?.status === 'inactive' ? ' · Inactive' : ''})`,
});

const BulkReassignmentModal: React.FC<BulkReassignmentModalProps> = ({ open, onClose, onReassigned }) => {
  const [relation, setRelation] = useState<BulkReassignRelation>('reporting_manager');
  const [allUsers, setAllUsers] = useState<UserApiItem[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [fromOption, setFromOption] = useState<CommonOption | null>(null);
  const [toOptionValue, setToOptionValue] = useState<CommonOption | null>(null);
  const [affectedUsers, setAffectedUsers] = useState<UserApiItem[]>([]);
  const [isLoadingAffected, setIsLoadingAffected] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const reset = (): void => {
    setRelation('reporting_manager');
    setFromOption(null);
    setToOptionValue(null);
    setAffectedUsers([]);
    setSelectedIds([]);
  };

  const handleClose = (): void => {
    if (isSubmitting) return;
    reset();
    onClose();
  };

  useEffect(() => {
    if (!open) return;

    const loadUsers = async (): Promise<void> => {
      try {
        setIsLoadingUsers(true);
        const response = await getUsers({ limit: 1000 });
        const payload = response.data;
        const users = Array.isArray(payload) ? payload : payload?.users ?? [];
        setAllUsers(users);
      } catch {
        showToast('error', 'Unable to load users for reassignment.');
      } finally {
        setIsLoadingUsers(false);
      }
    };

    void loadUsers();
  }, [open]);

  const relationConfig = RELATION_OPTIONS.find((option) => option.value === relation) ?? RELATION_OPTIONS[0];

  const fromOptions = useMemo(() => allUsers.map(toOption), [allUsers]);

  const fromUser = useMemo(
    () => allUsers.find((user) => user._id === fromOption?.value) ?? null,
    [allUsers, fromOption],
  );

  const toOptions = useMemo(() => {
    const activeUsers = allUsers.filter(
      (user) => user.meta?.status !== 'inactive' && user._id !== fromOption?.value,
    );
    const sameRoleUsers = fromUser
      ? activeUsers.filter((user) => user.role_name.trim().toLowerCase() === fromUser.role_name.trim().toLowerCase())
      : activeUsers;
    // Fall back to every active user if nobody else shares the departing
    // person's exact role — better than showing an empty picker.
    const source = sameRoleUsers.length > 0 ? sameRoleUsers : activeUsers;
    return source.map(toOption);
  }, [allUsers, fromOption, fromUser]);

  useEffect(() => {
    setToOptionValue(null);

    if (!fromOption) {
      setAffectedUsers([]);
      setSelectedIds([]);
      return;
    }

    const loadAffected = async (): Promise<void> => {
      try {
        setIsLoadingAffected(true);
        const response = await getUsers({ limit: 1000, [relationConfig.idField]: fromOption.value } as Record<string, string | number>);
        const payload = response.data;
        const users = Array.isArray(payload) ? payload : payload?.users ?? [];
        setAffectedUsers(users);
        setSelectedIds(users.map((user) => user._id));
      } catch {
        showToast('error', 'Unable to load affected users.');
        setAffectedUsers([]);
        setSelectedIds([]);
      } finally {
        setIsLoadingAffected(false);
      }
    };

    void loadAffected();
    // relationConfig is derived from `relation` each render — depending on it
    // directly would re-trigger this effect every render, so depend on `relation` instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromOption, relation]);

  const allSelected = affectedUsers.length > 0 && selectedIds.length === affectedUsers.length;

  const toggleSelectAll = (): void => {
    setSelectedIds(allSelected ? [] : affectedUsers.map((user) => user._id));
  };

  const toggleOne = (userId: string): void => {
    setSelectedIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId],
    );
  };

  const canSubmit =
    Boolean(fromOption) && Boolean(toOptionValue) && selectedIds.length > 0 && !isSubmitting;

  const handleSubmit = async (): Promise<void> => {
    if (!fromOption || !toOptionValue) return;

    try {
      setIsSubmitting(true);
      const response = await bulkReassignUsers({
        relation,
        from_user_id: fromOption.value,
        to_user_id: toOptionValue.value,
        user_ids: selectedIds,
      });

      if (!response.success) {
        throw new Error(response.message || 'Unable to reassign users.');
      }

      const repointed = response.data?.repointed_pending_approvals ?? 0;
      showToast(
        'success',
        `${response.data?.reassigned_count ?? selectedIds.length} user(s) reassigned.` +
          (repointed > 0 ? ` ${repointed} pending approval(s) were also moved to the new owner.` : ''),
      );
      reset();
      onReassigned();
      onClose();
    } catch (error) {
      const message =
        error instanceof ApiError ? error.message : error instanceof Error ? error.message : 'Unable to reassign users.';
      showToast('error', message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <CommonModal
      open={open}
      title="Bulk User Reassignment"
      onClose={handleClose}
      maxWidth="sm"
      actions={
        <>
          <CommonButton label="Cancel" variant="outlined" disabled={isSubmitting} onClick={handleClose} />
          <CommonButton
            label={isSubmitting ? 'Reassigning...' : `Reassign ${selectedIds.length || ''} User(s)`}
            disabled={!canSubmit}
            onClick={() => void handleSubmit()}
          />
        </>
      }
    >
      <Box>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Move everyone reporting to a Manager/Lead/QC/Dispute Person over to someone else — e.g. when they
            resign — without losing existing ticket or approval history.
          </Typography>

          <CommonDropdown
            label="Relation"
            value={relation}
            onChange={(event) => setRelation(event.target.value as BulkReassignRelation)}
            options={RELATION_OPTIONS}
          />

          <SearchableDropdown
            label="Currently Reports To"
            value={fromOption}
            onChange={setFromOption}
            options={fromOptions}
            placeholder="Search person being replaced"
            loading={isLoadingUsers}
          />

          {fromOption ? (
            <Box>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="subtitle2">
                  Affected Users {affectedUsers.length > 0 ? `(${affectedUsers.length})` : ''}
                </Typography>
                {affectedUsers.length > 0 ? (
                  <FormControlLabel
                    control={<Checkbox size="small" checked={allSelected} onChange={toggleSelectAll} />}
                    label="Select All"
                  />
                ) : null}
              </Stack>
              <Box
                sx={{
                  maxHeight: 220,
                  overflowY: 'auto',
                  border: '1px solid #e2e2e5',
                  borderRadius: 1.5,
                  p: isLoadingAffected || affectedUsers.length === 0 ? 2 : 0.5,
                }}
              >
                {isLoadingAffected ? (
                  <Typography variant="body2" color="text.secondary">
                    Loading affected users...
                  </Typography>
                ) : affectedUsers.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No users currently have this person set as their {relationConfig.label}.
                  </Typography>
                ) : (
                  <Stack spacing={0.25}>
                    {affectedUsers.map((user) => (
                      <Stack
                        key={user._id}
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ px: 0.5, py: 0.25, borderRadius: 1, '&:hover': { backgroundColor: '#f7f9f8' } }}
                      >
                        <Checkbox
                          size="small"
                          checked={selectedIds.includes(user._id)}
                          onChange={() => toggleOne(user._id)}
                        />
                        <Stack sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography variant="body2" noWrap>
                            {user.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {user.email} · {user.role_name}
                          </Typography>
                        </Stack>
                        <CommonBadge
                          label={user.meta?.status === 'inactive' ? 'Inactive' : 'Active'}
                          type={user.meta?.status === 'inactive' ? 'neutral' : 'closed'}
                        />
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Box>
            </Box>
          ) : null}

          <SearchableDropdown
            label="Reassign To (must be active)"
            value={toOptionValue}
            onChange={setToOptionValue}
            options={toOptions}
            placeholder={fromOption ? 'Search new owner' : 'Select "Currently Reports To" first'}
            disabled={!fromOption}
          />
        </Stack>
      </Box>
    </CommonModal>
  );
};

export default BulkReassignmentModal;
